import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { isInNextSixDays } from '@utils/product';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useState } from 'react';

import {
  getProductRef,
  getProductStatus,
} from '../../../../helpers/purchasing';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../../types/Purchasing';
import { formatTimedReleaseDate } from '../../../../utils/datetime';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import ProductDate from '../../ProductDate';
import { SignUpButton } from '../SignUpButton';
import { toClientUTCDateFormat } from '../utils';

type Props<T extends ProductPurchaseDetails> = {
  product: ProductType;
  purchasingController: ProductPurchasingController<T>;
};

export const TimedSubscribeCta: React.FC<Props<ProductPurchaseDetails>> = ({
  product,
  purchasingController,
}) => {
  const { t } = useTranslation('product');
  const clientLaunchDate = new Date(toClientUTCDateFormat(product.launchDate));
  const eta = formatTimedReleaseDate(product.launchDate, product.closingDate);
  const [showDialog, setShowDialog] = useState(false);

  const [emitter] = useAnalyticsEmitter();

  const handleHowToClick = useCallback(() => {
    emitter('', 'how clicked', {
      type: 'timed',
    });
  }, [, emitter]);

  return (
    <div className="flex w-full flex-1 flex-col space-y-6">
      <div className="flex w-full flex-1 flex-col">
        <p className="text-center font-bold">
          {eta === null
            ? isInNextSixDays(clientLaunchDate)
              ? t('subscriptionDate.timed.countdown')
              : t('subscriptionDate.timed.date')
            : isInNextSixDays(clientLaunchDate)
            ? t('subscriptionDate.timed.eta.countdown', { eta })
            : t('subscriptionDate.timed.eta.date', { eta })}
        </p>
        <p className="text-center">
          <ProductDate date={clientLaunchDate} />
        </p>
        <div className="mt-3 flex w-full flex-1 flex-col">
          <p className="text-center text-xs text-neutral-4">
            {t('subscriptionCta.timed.upcomingHelpText')}
          </p>
        </div>
      </div>
      <div className="flex w-full flex-1 flex-col items-center space-y-3">
        <SignUpButton
          labels={{ normal: t('notifyMe'), success: t('subscribed') }}
          product={product}
          productRef={getProductRef(purchasingController)}
          productStatus={getProductStatus(product, purchasingController)}
          showIcon
        />
        <button
          className="hyperlink"
          onClick={() => {
            setShowDialog(true);
            handleHowToClick();
          }}
          type="button"
        >
          {t('subscriptionCta.timed.learnMoreDialog.title')}
        </button>
        <SimpleDialog
          onClose={() => setShowDialog(false)}
          open={showDialog}
          title={t('subscriptionCta.timed.learnMoreDialog.title')}
        >
          <p className="mb-4">
            {t('subscriptionCta.timed.learnMoreDialog.paragraph1')}
          </p>
          <p>{t('subscriptionCta.timed.learnMoreDialog.paragraph2')}</p>
        </SimpleDialog>
      </div>
    </div>
  );
};
